import React from 'react';
import { Row, Col, Tag, Typography } from 'antd';
import { WorkerTelemetry, SAMPLE_NOTE, formatDuration, formatKm, formatMs } from '../utils/workerTelemetry';

const { Text } = Typography;

// The expanded row of the worker table. The row itself answers the four
// questions in one line each; everything that supports those answers — the
// percentiles, the queue, the country list, the outages, the day-by-day series
// — lives here, so no fact had to be dropped to make the table fit a 1280px
// screen, and none of it costs height until somebody asks for it.

const Section: React.FC<{ title: string; note?: string; children: React.ReactNode }> =
  ({ title, note, children }) => (
    <div style={{ marginBottom: 4 }}>
      <Text strong style={{ fontSize: 12 }}>{title}</Text>
      {note && <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>{note}</Text>}
      <div style={{ marginTop: 4 }}>{children}</div>
    </div>
  );

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ fontSize: 12, lineHeight: '18px' }}>
    <Text type="secondary" style={{ fontSize: 12 }}>{label} </Text>
    {children}
  </div>
);

const WorkerAnalyticsDetail: React.FC<{ worker: WorkerTelemetry }> = ({ worker: w }) => {
  const states = Object.entries(w.states || {}).sort((a, b) => b[1] - a[1]);
  const outages = w.outages || [];
  const countries = w.countries || [];

  return (
    <Row gutter={[16, 10]}>
      <Col xs={24} md={8}>
        <Section title="Check time" note="probe = duration − queue wait">
          <Fact label="probing">{formatMs(w.avgProbeMs)} avg</Fact>
          <Fact label="wall clock">
            proxy {Math.round(w.avgDurationMsProxy)} ms · ip {Math.round(w.avgDurationMsIp)} ms
          </Fact>
          <Fact label="waiting for a slot">
            {Math.round(w.avgWaitMs)} ms avg · {Math.round(w.maxWaitMs)} ms worst
          </Fact>
          <Fact label="shape">
            p50 {Math.round(w.p50DurationMs)} · p95 {Math.round(w.p95DurationMs)} · worst{' '}
            {Math.round(w.maxDurationMs)} ms, {w.measuredChecks.toLocaleString()} timed
          </Fact>
          <Fact label="dead targets">
            <Text type={w.avgLostMs > 0 ? 'warning' : undefined} style={{ fontSize: 12 }}>
              {Math.round(w.avgLostMs || 0)} ms
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {' '}of each proxy check, spent on targets that did not answer before another one
              did — part of the time above, and a property of the target: a dead URL at the head
              of the cascade adds its timeout to every check in the fleet
            </Text>
          </Fact>
        </Section>
      </Col>

      <Col xs={24} md={8}>
        <Section title="Load" note="sampled with availability, every 30s">
          <Fact label="slots used">{w.utilisationPercent}% avg</Fact>
          <Fact label="no free slot">{w.saturatedPercent}% of the time</Fact>
          <Fact label="queue">avg {Math.round(w.avgQueue)} · peak {w.maxQueue}</Fact>
        </Section>
        <Section title="Agreement" note="vs whoever checked the same target before">
          <Fact label="matched">
            {w.agreementPercent < 0
              ? 'no second opinions in this window'
              : `${w.agreementPercent}% of ${w.secondOpinions.toLocaleString()} compared`}
          </Fact>
          <Fact label="failures only it saw">{w.onlyThisWorkerFail.toLocaleString()}</Fact>
          <Fact label="failures only others saw">{w.onlyOtherFail.toLocaleString()}</Fact>
        </Section>
      </Col>

      <Col xs={24} md={8}>
        <Section title="Targets" note="distance to where the target's server is">
          <Fact label="distance">
            {formatKm(w.avgDistanceKm)} avg · {formatKm(w.maxDistanceKm)} max
          </Fact>
          <Fact label="nearest worker chosen">
            {w.geoOptimalPercent}% of {w.geoEvaluatedChecks.toLocaleString()} measurable checks
          </Fact>
          <Fact label="countries">
            {countries.length === 0
              ? '—'
              : `of ${w.countryKnownChecks.toLocaleString()} checks with a known country`}
          </Fact>
          <div style={{ marginTop: 4 }}>
            {countries.map((c) => (
              <Tag key={c.country} style={{ fontSize: 12, marginBottom: 4 }}>
                {c.country} {c.percent}% · {c.checks.toLocaleString()}
              </Tag>
            ))}
          </div>
        </Section>
      </Col>

      <Col xs={24} md={8}>
        <Section title="Verdicts produced">
          {states.map(([state, n]) => (
            <Tag
              key={state}
              style={{ fontSize: 12, marginBottom: 4 }}
              color={state === 'HEALTHY' ? 'success' : state === 'SUSPECT' ? 'default' : 'error'}
            >
              {state} {n.toLocaleString()}
            </Tag>
          ))}
          {!states.length && <Text type="secondary" style={{ fontSize: 12 }}>—</Text>}
        </Section>
      </Col>

      <Col xs={24} md={8}>
        <Section title="Offline periods" note={SAMPLE_NOTE}>
          <div style={{ maxHeight: 132, overflowY: 'auto' }}>
            {outages.length === 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>None recorded in this window</Text>
            )}
            {outages.slice(0, 12).map((o, i) => (
              <div key={i} style={{ fontSize: 12, lineHeight: '18px' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {new Date(o.from).toLocaleString()}
                </Text>
                {' → '}
                {o.to
                  ? <>{new Date(o.to).toLocaleString()} <Tag color="warning" style={{ fontSize: 12 }}>{formatDuration(o.seconds)}</Tag></>
                  : <Tag color="error" style={{ fontSize: 12 }}>still down</Tag>}
              </div>
            ))}
          </div>
        </Section>
      </Col>

      <Col xs={24} md={8}>
        <Section title="Per day">
          <div style={{ maxHeight: 132, overflowY: 'auto', fontSize: 12, lineHeight: '18px' }}>
            {w.days.map((d) => (
              <div key={d.day}>
                <Text type="secondary" style={{ fontSize: 12 }}>{d.day}</Text>
                {'  '}{d.checks.toLocaleString()} checks
                {d.availabilityPercent >= 0 && `  ·  ${d.availabilityPercent}% up`}
                {d.avgDurationMs > 0 && `  ·  ${formatMs(d.avgDurationMs)}`}
                {d.avgDistanceKm > 0 && `  ·  ${formatKm(d.avgDistanceKm)}`}
                {d.disconnects > 0 && `  ·  ${d.disconnects} drops`}
              </div>
            ))}
          </div>
        </Section>
      </Col>
    </Row>
  );
};

export default WorkerAnalyticsDetail;
